"use client"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import React, { useEffect } from "react"
import { useAccount } from "wagmi"
import { useLatest, useLocalStorage } from "react-use"
import { IS_PROD } from "../../IS_PROD"

export const VaultContext = React.createContext<
  | {
      onBehalfOf: `0x${string}` | null | undefined
      setOnBehalfOf: (vault: `0x${string}` | null | undefined) => void
    }
  | undefined
>(undefined)

export const VaultProvider = ({ children }: { children: React.ReactNode }) => {
  const { address } = useAccount()
  const [onBehalfOf, setOnBehalfOf] = useLocalStorage<
    `0x${string}` | null | undefined
  >(`vault-${address}`, null)

  // we reset the behalf of if the user changes address
  const lastAddress = useLatest(address)
  useEffect(() => {
    if (address && address !== lastAddress.current) {
      setOnBehalfOf(null)
    }
  }, [address, setOnBehalfOf, lastAddress])
  return (
    <VaultContext.Provider value={{ onBehalfOf, setOnBehalfOf }}>
      {children}
    </VaultContext.Provider>
  )
}
VaultProvider.displayName = "VaultProvider"

export const useVaultsQuery = (
  address: `0x${string}` | undefined,
  lazyLoad?: boolean,
) => {
  const chain = IS_PROD ? "mainnet" : "sepolia"

  return useQuery({
    queryKey: ["modular-perms", chain, address],
    queryFn: async () => {
      const response = await axios.get(
        `/api/blessnet/addresses/${address}/permissions?chain=${chain}`,
      )
      return response.data
    },
    enabled: Boolean(address) && lazyLoad,
  })
}

export const useVaults = (lazyLoad?: boolean) => {
  const context = React.useContext(VaultContext)
  const { address } = useAccount()

  if (context === undefined) {
    throw new Error("useVault must be used within a VaultProvider")
  }
  const query = useVaultsQuery(address, lazyLoad)
  return {
    ...context,
    ...query,
    address,
    effectiveAddress: context.onBehalfOf || address,
  }
}
